// src/pages/Login.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [shake, setShake] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const errorRef = useRef(null);
  const clearTimerRef = useRef(null);
  const bannerTimerRef = useRef(null);

  const styles = `
    @keyframes shake {0%{transform:translateX(0)}20%{transform:translateX(-6px)}40%{transform:translateX(6px)}60%{transform:translateX(-4px)}80%{transform:translateX(4px)}100%{transform:translateX(0)}}
    .login-root{min-height:100vh;min-height:100dvh;display:flex;flex-direction:column;background:linear-gradient(to bottom,#1e293b,#0f172a);color:#fff}
    .page-body{flex:1;display:flex;justify-content:center;align-items:center;padding:16px;padding-top:72px;padding-bottom:16px}
    .page-container{width:100%;max-width:400px;margin:0 auto}
    .card{background:#1e293b;border:1px solid #334155;border-radius:16px;box-shadow:0 8px 32px rgba(0,0,0,.2);padding:24px 20px}
    .input-field{width:100%;padding:16px;background:#334155;border:1px solid #475569;color:#fff;border-radius:12px;outline:none;transition:border-color .2s,box-shadow .2s;font-size:16px;-webkit-appearance:none;line-height:1.5;min-height:52px}
    .input-field:focus{border-color:#3b82f6;box-shadow:0 0 0 3px rgba(59,130,246,.1)}
    .input-field.error{border-color:#ef4444}
    .submit-btn{width:100%;padding:16px;background:#3b82f6;color:#fff;border:none;border-radius:12px;cursor:pointer;font-weight:600;font-size:17px;transition:transform .1s,opacity .2s;min-height:56px}
    .submit-btn:active{transform:scale(.98)}
    .submit-btn:disabled{opacity:.7;cursor:not-allowed;transform:none}
    .error-message{color:#ef4444;margin-bottom:16px;padding:14px;background:rgba(239,68,68,.1);border-radius:12px;border:1px solid rgba(239,68,68,.3);font-size:14px;line-height:1.4}
    .mobile-link{color:#94a3b8;text-decoration:none;font-size:15px;padding:12px 0;display:inline-block;min-height:44px}
    .back-btn{
      position:fixed; top:max(12px, env(safe-area-inset-top)); left:max(12px, env(safe-area-inset-left));
      z-index:100; display:inline-flex;align-items:center;gap:8px; padding:12px 16px;
      background:rgba(15,23,42,.7); backdrop-filter:blur(4px); border:1px solid #334155; color:#fff;border-radius:12px;cursor:pointer;font-size:15px;min-height:44px;
    }
    .back-btn svg{display:block}
    .label-text{display:block;margin-bottom:8px;font-size:15px;font-weight:500}
    .title{font-size:24px;margin-bottom:20px;text-align:center;font-weight:600}
    .divider{margin:20px auto;border:none;border-top:1px solid #475569;width:100%;max-width:200px}
    .helper-text{margin-top:8px;font-size:13px;color:#94a3b8;line-height:1.4}
    .signup-text{margin-top:8px;color:#94a3b8;font-size:14px;line-height:1.5}

    /* wrapper per centratura dell'icona rispetto all'input */
    .field-wrap{ position:relative; }

    /* Toggle icona password centrata verticalmente nel riquadro input */
    .password-toggle{
      position:absolute; right:10px; top:50%; transform:translateY(-50%);
      display:inline-flex; align-items:center; justify-content:center;
      width:36px; height:36px; border:none; background:transparent;
      color:#cbd5e1; cursor:pointer; padding:0;
    }
    .password-toggle:hover{color:#ffffff}
    .password-toggle:focus-visible{outline:2px solid rgba(59,130,246,.6); outline-offset:2px; border-radius:8px}
    .password-toggle svg{display:block; width:20px; height:20px}

    @media (max-width: 380px) {
      .page-body{padding:12px;padding-top:64px;padding-bottom:12px}
      .card{padding:20px 16px}
      .title{font-size:22px;margin-bottom:16px}
      .password-toggle{right:8px; width:32px; height:32px}
    }

    @media (max-height: 600px) {
      .page-body{padding-top:60px;padding-bottom:8px}
      .card{padding:20px 16px}
    }

    @supports (-webkit-touch-callout:none){.login-root{min-height:-webkit-fill-available}}
  `;

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') handleGoBack(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
      clearTimerRef.current = setTimeout(() => setError(''), 6000);
    }
    return () => { if (clearTimerRef.current) clearTimeout(clearTimerRef.current); };
  }, [error]);

  useEffect(() => {
    if (showBanner) {
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
      bannerTimerRef.current = setTimeout(() => setShowBanner(false), 5000);
    }
    return () => { if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current); };
  }, [showBanner]);

  const handleGoBack = () => {
    if (window.history.length <= 1) navigate('/', { replace: true });
    else navigate(-1);
  };

  const getErrorMessage = (err) => {
    const code = err?.code || '';
    switch (code) {
      case 'auth/invalid-email':          return 'Invalid email format';
      case 'auth/user-disabled':          return 'Account disabled. Contact support.';
      case 'auth/too-many-requests':      return 'Too many attempts. Try again later or reset your password.';
      case 'auth/network-request-failed': return 'Network error. Check your connection and try again';
      default:                            return 'Invalid email or password';
    }
  };

  const fail = (msg) => {
    setError(msg || 'Invalid email or password');
    setShowBanner(true);
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { fail('Please enter email and password'); return; }
    setError('');
    setIsSubmitting(true);
    try {
      const result = await login(email.trim(), password);
      const userObj = result?.user ?? result;
      if (!userObj) { fail('Invalid email or password'); return; }
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      fail(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-root">
      <style>{styles}</style>

      <button
        onClick={handleGoBack}
        aria-label="Go back"
        title="Go back"
        className="back-btn"
        type="button"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back
      </button>

      <main className="page-body">
        <div className="page-container">
          <div className="card" style={{ animation: shake ? 'shake 500ms ease' : 'none' }}>
            <h2 className="title">Log in to your account</h2>

            {error && (
              <div
                ref={errorRef}
                tabIndex={-1}
                role="alert"
                aria-live="assertive"
                className="error-message"
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="email" className="label-text">Email</label>
                <input
                  id="email"
                  type="email"
                  placeholder="example@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={Boolean(error)}
                  className={`input-field ${error ? 'error' : ''}`}
                  required
                  autoComplete="email"
                />
              </div>

              {/* PASSWORD FIELD */}
              <div style={{ marginBottom: 24 }}>
                <label htmlFor="password" className="label-text">Password</label>

                <div className="field-wrap">
                  <input
                    id="password"
                    type={showPwd ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-invalid={Boolean(error)}
                    className={`input-field ${error ? 'error' : ''}`}
                    required
                    autoComplete="current-password"
                    style={{ paddingRight: 48 }}
                  />

                 <button
  type="button"
  aria-label={showPwd ? 'Hide password' : 'Show password'}
  title={showPwd ? 'Hide password' : 'Show password'}
  onMouseDown={(e) => e.preventDefault()}
  onClick={() => setShowPwd(v => !v)}
  className="password-toggle"
>
  {showPwd ? (
    // OCCHIO NORMALE quando la password è visibile
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
    </svg>
  ) : (
    // OCCHIO BARRATO quando la password NON è visibile
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M9.88 4.24A11.76 11.76 0 0121 12c-1.6 2.9-4.73 6-9 6-1.29 0-2.5-.24-3.6-.68M6.11 6.11A11.76 11.76 0 003 12c1.6 2.9 4.73 6 9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )}
</button>

                </div>

                {error && (error.toLowerCase().includes('invalid') || error.toLowerCase().includes('too many')) && (
                  <p className="helper-text" style={{ color: '#fca5a5', marginTop: 8 }}>
                    Check your email and password, then try again.
                  </p>
                )}
              </div>

              <button type="submit" disabled={isSubmitting} className="submit-btn">
                {isSubmitting ? 'Logging in...' : 'Log In'}
              </button>
            </form>

            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <Link to="/forgot-password" className="mobile-link" style={{ marginBottom: '16px', display: 'block' }}>
                Forgot password?
              </Link>

              <hr className="divider" />

              <p className="signup-text">Don't have an account?</p>
              <p className="helper-text" style={{ marginTop: '8px' }}>
                Only clients can have access to the private area. Buy a product to start your journey!
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
